import { Button, DynamicContainer, Form, FormContext, FormSection } from '@hai/ui-react';
import { ButtonStateType } from '@hai/ui-react/dist/types';
import { FormikProps } from 'formik';
import React, { Ref, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormTextfield } from 'src/js/component/base/form/form-textfield';
import { FormBuilder, FormFieldType, FormLayout } from 'src/js/component/form-builder';
import systemInfo, { systemInfoCapabilities } from 'src/js/data/systemInfo';
import { triggerEvent, watchEvent } from 'src/js/events';
import { useStores } from 'src/js/hook/use-stores';
import streams, {
  addRecording,
  addStream,
  deleteRecording,
  deleteStream,
  editRecording,
  fetchStream,
  fetchStreamStatistics,
  updateStream,
} from 'src/js/pages/streaming/stream/stream-store';
import { useWatchObject } from 'src/js/store/use-watch';
import { constant } from 'src/js/constant';
import { changes } from 'src/js/util/formik-helper';
import { submitAndValidate } from 'src/js/validator/validator';

import audioEncoders from '../audio-encoder/audio-encoder-store';
import { metadataName, metadataNameOrConstructed } from '../metadata/metadata-models';
import metadataModels from '../metadata/metadata-store';
import scteModels from '../scte/scte-store';
import videoEncoders from '../video-encoder/video-encoder-store';
import { StreamEditAccessControlForm, StreamEditAccessControlFormRef } from './stream-edit-access-control';
import {
  FecRTP,
  isEmptyContentSource,
  MetadataEncapsulation,
  MpegProFecLevel,
  NetworkInterfaces,
  RecordFormat,
  RecordLimitSizeUnit,
  RecordLimitType,
  SRTAuthentication,
  SRTAuthenticationListener,
  SRTAuthenticationOthers,
  SRTEncryption,
  SRTMode,
  SrtPathRedundancyMode,
  SrtPathRedundancyModeOffered,
  SrtRedundancyPathRole,
  StreamEncapsulation,
  StreamShaping,
  StreamViewModel,
} from './stream-models';
import { streamValidationSchema } from './stream-validations';
import { enumValues } from 'src/js/helper/enum-helper';
import { VIDEO_CODEC } from '../video-encoder/video-encoder-models';
import { AudioAlgorithmEnum } from '../audio-encoder/audio-encoder-models';
import { useMutable } from 'src/js/hook/use-mutable';
import { notificationHandler } from 'src/js/notification/notification-handler';
import { isNilOrEmpty } from 'src/js/util/global-util';
import { createAndDispatchNotification } from 'src/js/notification/notification-helper';
import { NotificationVariant } from 'src/js/notification/notification';

interface Props {
  model: StreamViewModel;
  onCancel?: VoidFunction;
}

export interface StreamAddFormRef {
  submitStream: () => Promise<void>;
}

export const StreamEdit = forwardRef((props: Props, ref: Ref<StreamAddFormRef>) => {
  const { model, onCancel } = props;
  const { t } = useTranslation();
  const { sessionStore } = useStores();

  const formRef = useRef<FormikProps<StreamViewModel>>();
  const accessControlFormRef = useRef<StreamEditAccessControlFormRef>();
  const nameField = useRef<HTMLInputElement>();
  const newStream = model.id === -1;
  const maxStreamContentSources = systemInfo.maxStreamContentSources;

  // setting a state here prevents live update from modifying the edit form
  const [settings, setSettings] = useState<StreamViewModel>(model);
  const [buttonState, setButtonState] = useState<ButtonStateType>(undefined);
  const [showTsFields, setShowTsFields] = useState(false);
  const [trafficShapingWasTweaked, setTrafficShapingWasTweaked] = useMutable<boolean | StreamShaping>(false); // WALU-246 issue from the comment section
  const [sourcePortWasTweaked, setSourcePortWasTweaked] = useMutable<boolean | number | ''>(false); // WALU-246 issue from the comment section

  useEffect(() => {
    if (!newStream) {
      fetchStreamStatistics(model, t, setSettings); // refetch individual is required to load sap parameters
    } else {
      nameField.current?.focus?.();
    }
  }, []);

  const [resetOnNextReset, setResetOnNextReset] = useMutable(false);
  watchEvent('preset-load', () => {
    setResetOnNextReset(true);
  });

  useWatchObject(streams, (_updates) => {
    // compare if any changes from upstream since form was opened (settings vs _updates) and notify
    if (resetOnNextReset()) {
      const stream = streams.find((e) => e.id === model.id);
      if (stream) {
        setSettings(stream); // update edit view with newest model
        formRef.current.resetForm({ values: stream });
        setResetOnNextReset(false);
      }
    }
  });

  const handleSubmit = async (values: any) => {
    setButtonState('pending');

    if (newStream) {
      return addStream(formRef.current, formRef.current.values, t, false).then(async (res) => {
        setButtonState(undefined);
        if (res.ok) {
          // add recording if any
          if (formRef.current.values.recording?.enabled && res.data?.info?.id) {
            const newStreamId = (formRef.current.values.id = res.data.info.id); // save new ID for the record request
            const recordResponse = await addRecording(formRef.current, formRef.current.values, t);
            if (recordResponse.ok) {
              notificationHandler(
                res,
                isNilOrEmpty(res.data?.info?.name)
                  ? t('stream.notifications.addSuccessEmptyName')
                  : t('stream.notifications.addSuccess', { name: res.data?.info?.name }),
                isNilOrEmpty(res.data?.info?.name)
                  ? t('stream.notifications.addErrorEmptyName')
                  : t('stream.notifications.addError', { name: res.data?.info?.name }),
                t,
              );

              if (
                recordResponse.data?.warning?.includes('Disk full') ||
                recordResponse.data?.warning?.includes('Insufficient storage space')
              ) {
                // but since stream creation worked, we have to close the modal NKE-3983
                createAndDispatchNotification(recordResponse.data?.warning, NotificationVariant.WARNING, t);
                triggerEvent('preset-update');
                return res; // simulate success close modal
              } else if (recordResponse.data?.warning && recordResponse.data.warning.length > 0) {
                // catch all for record warnings - stream create
                createAndDispatchNotification(recordResponse.data?.warning, NotificationVariant.WARNING, t);
                triggerEvent('preset-update');
              }
              return res; // success
            } else if (recordResponse.status === 400) {
              // user input error
              // delete stream and keep modal open
              deleteStream({ id: newStreamId } as StreamViewModel);

              if ((recordResponse.data as unknown as string)?.includes('Recording size limit is too small')) {
                formRef.current.setFieldError('recording.limitSize', ' ');
              } else if ((recordResponse.data as unknown as string)?.includes('Recording segment size is too small')) {
                formRef.current.setFieldError('recording.segmentSize', ' ');
              } else if (
                (recordResponse.data as unknown as string)?.includes(
                  'Recording segments larger or equal to recording size',
                )
              ) {
                formRef.current.setFieldError('recording.limitSize', ' ');
                formRef.current.setFieldError('recording.segmentSize', ' ');
              }
              return; // dont reset form
            } else {
              // error
              // delete stream and keep modal open
              deleteStream({ id: newStreamId } as StreamViewModel);
              return; // keep modal open
            }
          }

          triggerEvent('preset-update');
        }
        return res;
      });
    } else {
      // edit existing stream
      // Strip computed/UI properties that shouldn't trigger API updates
      const stripRecordingFromStream = (obj: any) => {
        if (!obj) {
          return obj;
        }
        const { recording, ...stripped } = obj;
        return stripped;
      };

      const streamInitialStripped = stripRecordingFromStream(formRef.current.initialValues);
      const streamValuesStripped = stripRecordingFromStream(formRef.current.values);
      const changedStreamObj = changes(streamValuesStripped, streamInitialStripped);
      let streamChanged = true;
      if (Object.keys(changedStreamObj).length === 0) {
        streamChanged = false;
      }
      let recordChanged = false;
      const changedRecordObj = changes(formRef.current.values.recording, formRef.current.initialValues?.recording);
      if (Object.keys(changedRecordObj).length > 0) {
        recordChanged = true;
      }
      // console.log('changedStream', changedStreamObj);

      // check if we need to delete record
      if (
        formRef.current.initialValues?.recording?.enabled === true &&
        formRef.current.values.recording?.enabled === false
      ) {
        // User turned off recording, delete recording
        const recordResponse = await deleteRecording(formRef.current.values.recording.id, t);
        if (recordResponse.ok) {
          // fetch stream again
          await fetchStream(model, t);
        } else {
          // delete error
          return; // dont reset form
        }
      } else if (formRef.current.values.recording?.enabled === true) {
        // record toggle is on

        if (streamChanged === false && recordChanged) {
          // record only changes (no stream changes)

          const recordResponse = await editRecording(formRef.current, formRef.current.values, t); // apply changes
          setButtonState(undefined);
          // console.log('recordResponse', recordResponse);
          if (!recordResponse.ok) {
            // error
            // dont reset form
            return recordResponse;
          }

          createAndDispatchNotification(
            isNilOrEmpty(formRef.current.values?.name)
              ? t('stream.notifications.updateSuccessEmptyName')
              : t('stream.notifications.updateSuccess', { name: formRef.current.values?.name }),
            NotificationVariant.SUCCESS,
            t,
          );

          if (recordResponse.data?.warning && recordResponse.data.warning.length > 0) {
            // catch all for record warnings - stream edit
            createAndDispatchNotification(recordResponse.data?.warning, NotificationVariant.WARNING, t);
          }

          // success
          await fetchStream(model, t);
          const stream = streams.find((e) => e.id === model.id);
          setSettings(stream); // update edit view with newest model
          formRef.current.resetForm({ values: stream });
          triggerEvent('preset-update');
          return recordResponse;
        } // end of record only changes with no stream changes
      }

      // update stream
      return updateStream(formRef.current, values, t).then(async (res: any) => {
        setButtonState(undefined);
        if (res.ok) {
          // console.log('formRef.current.values', formRef.current.values);
          if (formRef.current.values.recording?.enabled === true) {
            // verify if any change to recording.
            const recordResponse = await editRecording(formRef.current, formRef.current.values, t);
            if (!recordResponse.ok) {
              return recordResponse; // dont reset form
            }
            if (recordResponse.data?.warning && recordResponse.data.warning.length > 0) {
              // catch all for record warnings - stream edit
              createAndDispatchNotification(recordResponse.data?.warning, NotificationVariant.WARNING, t);
            }
            // fetch stream again
            await fetchStream(model, t);
          }

          const stream = streams.find((e) => e.id === model.id);
          setSettings(stream); // update edit view with newest model
          formRef.current.resetForm({ values: stream });
          triggerEvent('preset-update');
        }
        return res;
      });
    }
  };

  const checkTsFields = (formContext: FormikProps<StreamViewModel>) => {
    // Open up TS Settings if an error occured in sub-form
    if (
      Object.keys(formContext.errors).some((v) =>
        [
          'videoPids',
          'audioPids',
          'metadataPids',
          'sctePids',
          'pcrPid',
          'pmtPid',
          'tsStreamId',
          'programNumber',
        ].includes(v),
      )
    ) {
      setShowTsFields(true);
    }
  };

  const submitStream = () => {
    return submitAndValidate(formRef.current, t).then((res) => {
      if (formRef.current && formRef.current?.isValid === false) {
        checkTsFields(formRef.current);
      }
      return res;
    });
  };

  useImperativeHandle(ref, () => ({
    submitStream,
  }));

  const onChangeSrtMode = (e: any) => {
    if (sourcePortWasTweaked() !== false) {
      if (e.target.value !== SRTMode.RENDEZVOUS) {
        formRef.current.setFieldValue('sourcePort', sourcePortWasTweaked());
        setSourcePortWasTweaked(false);
      }
    }

    if (e.target.value === SRTMode.RENDEZVOUS) {
      setSourcePortWasTweaked(formRef.current.values.sourcePort);
      formRef.current.setFieldValue('sourcePort', formRef.current.values.port);
    }
    if (accessControlFormRef?.current) {
      accessControlFormRef.current.onChangeSrtMode?.();
    }
  };

  const onChangeVideo = (e: any) => {
    if (
      formRef.current.initialValues?.video[0]?.id === e.target.value ||
      (formRef.current.initialValues?.video?.length === 0 && e.target.value === -1)
    ) {
      formRef.current.setFieldValue('video', formRef.current.initialValues?.video);
    } else {
      formRef.current.setFieldValue('video', [{ id: e.target.value }]);
    }
  };

  const onChangeScte = (e: any) => {
    if (
      formRef.current.initialValues?.scte[0]?.id === e.target.value ||
      (formRef.current.initialValues?.scte?.length === 0 && e.target.value === -1)
    ) {
      formRef.current.setFieldValue('scte', formRef.current.initialValues?.scte);
    } else {
      formRef.current.setFieldValue('scte', [{ id: e.target.value }]);
    }
  };

  const onChangePort = (e: any) => {
    if (formRef.current.values.srtMode === SRTMode.RENDEZVOUS) {
      setSourcePortWasTweaked(formRef.current.values.sourcePort);
      formRef.current.setFieldValue('sourcePort', e.target.value);
    }
  };

  let encapsulationOptions = enumValues(StreamEncapsulation).map((value) => ({
    value: value,
    label: t(`stream.enums.encapsulation.${value.toString() as string}`),
  }));
  if (!systemInfoCapabilities.hasSRT()) {
    encapsulationOptions = encapsulationOptions.filter((option) => option.value !== StreamEncapsulation.SRT);
  }
  if (!systemInfo.hasWHIP) {
    encapsulationOptions = encapsulationOptions.filter((option) => option.value !== StreamEncapsulation.WHIP);
  }

  // combined video + first audio encoder bitrate (Kbps) currently selected on the form, used to validate min recording/segment size
  // clone of C function _CheckForMinRecordingLength() in sessmgr_daemon/sm_storage.c
  const getTotalRecordingBitrateInKbps = (): number => {
    const currentValues = formRef.current?.values;
    const videoEncoderId = currentValues?.video?.[0]?.id;
    const audioEncoderId = currentValues?.audio?.[0]?.id;
    const selectedVideoEncoder = videoEncoders.find((v) => v.id === videoEncoderId);
    const selectedAudioEncoder = audioEncoderId > 0 ? audioEncoders.find((a) => a.id === audioEncoderId) : undefined;
    return Number(selectedVideoEncoder?.bitrate ?? 0) + Number(selectedAudioEncoder?.bitRate ?? 0);
  };

  return (
    <Form
      initialValues={settings}
      defaultValidation={true}
      handleSubmit={handleSubmit}
      className="hai-mt-n8"
      restValidationProps={{
        enableReinitialize: false, // "false" requires manual resetForm() above. We can then do partial update of the form while it's opened.
        validationSchema: streamValidationSchema(t, getTotalRecordingBitrateInKbps),
        innerRef: formRef,
      }}
    >
      <FormContext.Consumer>
        {(formContext: FormikProps<StreamViewModel>) => {
          const values = formContext.values;
          let showRedundancy = false;
          let showRedundancyPrimaryPathRows = false;
          const audioCount = values.audio?.length;
          const videoCount = values.video?.length;
          const videoEncoderSelected = videoEncoders.find((v) => v.id === values.video[0]?.id);
          const videoEncoderRealCount = videoCount === 0 ? 0 : values.video.filter((v) => v.id !== -1).length;
          const firstAudioEncoderSelected = audioEncoders.find((a) => a.id === values.audio[0]?.id);
          const metadataCount = values.metadata?.length;
          const scteCount = values.scte?.length;
          const totalSourceCount = audioCount + videoCount + metadataCount + scteCount;
          const disableAddAll = totalSourceCount >= maxStreamContentSources ? true : false;
          const disableAudioAdd =
            audioCount === 0 ||
            (audioCount === 1 && values.audio[0].id === -1) ||
            values.encapsulation === StreamEncapsulation.WHIP; // if 'None' selected
          const disableMetadataAdd = metadataCount === 0 || (metadataCount === 1 && values.metadata[0].id === -1); // if 'None' selected

          if (values.encapsulation === StreamEncapsulation.SRT && values.srtMode === SRTMode.CALLER) {
            if (values.srtRedundancyMode === SrtPathRedundancyMode.NONE) {
              showRedundancy = false;
            } else {
              showRedundancy = true;
              if (values.srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_ACTIVE) {
                showRedundancyPrimaryPathRows = false;
              } else {
                showRedundancyPrimaryPathRows = true;
              }
            }
          }
          let showNonRedundantCallerRendezvous = false;
          if (values.encapsulation === StreamEncapsulation.SRT && showRedundancy === false) {
            if (values.srtMode === SRTMode.CALLER || values.srtMode === SRTMode.RENDEZVOUS) {
              showNonRedundantCallerRendezvous = true;
            }
          }
          const isListener = values.encapsulation === StreamEncapsulation.SRT && values.srtMode === SRTMode.LISTENER;

          let isAnyMetadata = false;
          if (systemInfoCapabilities.hasMetadata() && values.metadata && values.metadata.length > 0) {
            values.metadata.forEach((item) => {
              if (item.id !== -1 && item.id >= 0) {
                isAnyMetadata = true;
              }
            });
          }

          const fixSrtFields = (
            srtMode: SRTMode,
            srtRedundancyMode: SrtPathRedundancyMode,
            authentication: SRTAuthentication,
          ) => {
            if (srtMode == SRTMode.LISTENER) {
              if (authentication === undefined || authentication === SRTAuthentication.NONE) {
                formContext.setFieldValue('authentication', SRTAuthenticationListener.AUTO);
              }
              if (srtRedundancyMode === undefined || srtRedundancyMode === SrtPathRedundancyMode.NONE) {
                formContext.setFieldValue('srtRedundancyMode', SrtPathRedundancyMode.OPTIONAL);
              }
            } else {
              // non-listener
              if (authentication === undefined || authentication === SRTAuthentication.AUTO) {
                formContext.setFieldValue('authentication', SRTAuthenticationOthers.NONE);
              }
              if (srtRedundancyMode === undefined || srtRedundancyMode === SrtPathRedundancyMode.OPTIONAL) {
                formContext.setFieldValue('srtRedundancyMode', SrtPathRedundancyMode.NONE);
              }
            }
          };

          let applyButtonEnabled = formContext.dirty;
          // Strip all properties except id from each audio array element in both objects
          const stripAudio = (obj: StreamViewModel) => {
            if (!obj || !Array.isArray(obj.audio)) {
              return obj;
            }
            return {
              ...obj,
              audio: obj.audio.map((a: any) => ({ id: a.id })),
            };
          };
          const valuesStripped = stripAudio(values);
          const initialValuesStripped = stripAudio(formContext.initialValues);
          const changed = changes(valuesStripped, initialValuesStripped);
          delete (changed as any).audio_multi;
          delete (changed as any).metadata_multi;

          if (changed.passphrase !== undefined && changed.passphrase === '') {
            // console.log('we zeroed out passphrase. Remove from changed object');
            delete changed.passphrase;
          }

          if (values.encapsulation !== StreamEncapsulation.RTP) {
            delete changed.columns;
            delete changed.rows;
            delete changed.fecRtp;
            delete changed.level;
            delete changed.blockAligned;
          }

          if (values.encapsulation !== StreamEncapsulation.SRT) {
            delete changed.adaptive;
            delete changed.authentication;
            delete changed.encKeyLength;
            delete changed.latency;
            delete changed.passphrase;
            delete changed.sourcePort;
            delete changed.srtAccessControlPublishingIdType;
            delete changed.srtAccessPublishingID;
            delete changed.srtAccessUserName;
            delete changed.srtAccessResourceName;
            delete changed.srtMode;
            delete changed.srtRedundancyMode;
            delete changed.srtRedundancyPath1Role;
            delete changed.srtRedundancyPath1Address;
            delete changed.srtRedundancyPath2Address;
            delete changed.srtRedundancyPath1Port;
            delete changed.srtRedundancyPath2Port;
            delete changed.srtRedundancyPath1SourcePort;
            delete changed.srtRedundancyPath2SourcePort;
            delete changed.srtRedundancyPath1Name;
            delete changed.srtRedundancyPath2Name;
            delete changed.srtRedundancyPath2NetworkInterface;
            delete changed.srtListenerSecondPort;
            delete changed.srtStreamId;
          }

          if (values.encapsulation === StreamEncapsulation.SRT && values.srtMode === SRTMode.CALLER) {
            delete changed.srtListenerSecondPort;
          }

          if (values.encapsulation === StreamEncapsulation.SRT && values.srtMode === SRTMode.LISTENER) {
            delete changed.address;
            delete changed.authentication;
            delete changed.sourcePort;
            delete changed.srtRedundancyMode;
            delete changed.srtRedundancyPath1Role;
            delete changed.srtRedundancyPath1Address;
            delete changed.srtRedundancyPath2Address;
            delete changed.srtRedundancyPath1Port;
            delete changed.srtRedundancyPath2Port;
            delete changed.srtRedundancyPath1SourcePort;
            delete changed.srtRedundancyPath2SourcePort;
            delete changed.srtRedundancyPath1Name;
            delete changed.srtRedundancyPath2Name;
            delete changed.srtRedundancyPath2NetworkInterface;
            delete changed.srtListenerSecondPort;
          }

          if (values.encapsulation === StreamEncapsulation.SRT && values.srtMode === SRTMode.RENDEZVOUS) {
            delete changed.srtRedundancyMode;
            delete changed.srtRedundancyPath1Name;
            delete changed.srtRedundancyPath1SourcePort;
            delete changed.srtRedundancyPath2Address;
            delete changed.srtRedundancyPath2Name;
            delete changed.srtRedundancyPath2SourcePort;
          }

          if (values.encapsulation !== StreamEncapsulation.WHIP) {
            delete changed.bearerToken;
            delete changed.url;
          }

          if (changed.metadata !== undefined) {
            if (isEmptyContentSource(formContext.initialValues.metadata) && isEmptyContentSource(values.metadata)) {
              // both are empty written in different ways
              //console.log('both content source are empty', formContext.initialValues.metadata, values.metadata);
              delete changed.metadata;
            }
          }
          if (changed.audio !== undefined) {
            if (isEmptyContentSource(formContext.initialValues.audio) && isEmptyContentSource(values.audio)) {
              // both are empty written in different ways
              //console.log('both content source are empty', formContext.initialValues.audio, values.audio);
              delete changed.audio;
            }
          }

          if (changed.metadataEncapsulation) {
            if (!isAnyMetadata) {
              // field is hidden
              delete changed.metadataEncapsulation;
            }
          }

          if (changed.sap) {
            if (values.sap.advertise === false) {
              // field is hidden
              if (values.sap.advertise === false && initialValuesStripped.sap?.advertise === true) {
                // user turned off sap
              } else {
                // dont change hidden fields
                delete changed.sap;
              }
            }
          }

          if (Object.keys(changed).length === 0) {
            applyButtonEnabled = false;
          }

          // console.log('changed', changed, formContext.initialValues);
          const srtRedundantEnabled =
            (values.srtMode === SRTMode.CALLER && values.srtRedundancyMode !== SrtPathRedundancyMode.NONE) ||
            (values.srtMode === SRTMode.LISTENER && Number(values.srtListenerSecondPort) > 0);
          if (srtRedundantEnabled && values.adaptive === true) {
            formContext.setFieldValue('adaptive', false);
            if (trafficShapingWasTweaked() !== false) {
              formContext.setFieldValue('shaping', trafficShapingWasTweaked() as StreamShaping);
              setTrafficShapingWasTweaked(false);
            }
            // console.log('Updated network adaptive encoding to false due to redundancy being enabled');
          }

          const onChangeListenerSecondPort = (e: any) => {
            if (Number(e.target.value) > 0) {
              // did we change network adaptive checkbox?
              formContext.setFieldValue('adaptive', false);
              formContext.setFieldTouched('adaptive');
              if (trafficShapingWasTweaked() !== false) {
                formContext.setFieldValue('shaping', trafficShapingWasTweaked() as StreamShaping);
                setTrafficShapingWasTweaked(false);
              }
            } else {
              // we remove redundancy
            }
          };

          const onChangeNetworkAdaptive = (e: any) => {
            if (e.target.checked) {
              if (values.shaping !== StreamShaping.CVBR) {
                setTrafficShapingWasTweaked(values.shaping);
                formContext.setFieldValue('shaping', StreamShaping.CVBR);
              }
            } else {
              // unchecked
              if (values.shaping === StreamShaping.CVBR && trafficShapingWasTweaked() !== false) {
                formContext.setFieldValue('shaping', trafficShapingWasTweaked() as StreamShaping);
                setTrafficShapingWasTweaked(false);
              }
            }
          };

          const onChangeTrafficShaping = (_e: any) => {
            if (trafficShapingWasTweaked()) {
              setTrafficShapingWasTweaked(false);
            }
          };
          // console.log(values.recording);
          return (
            <>
              <DynamicContainer minColumns={2} maxColumns={2} className="mk-mt-panel-name-align">
                <FormTextfield
                  inputRef={nameField}
                  name="name"
                  mask
                  placeholder={values.name}
                  title={t('general.name')}
                  className="max-width"
                  autoFocus={newStream}
                />
                {!newStream && !sessionStore.isUser() && (
                  <div className="d-flex ml-auto hai-mt-panel-apply-sticky">
                    <Button className="hai-mr-6" onClick={onCancel}>
                      {t('general.cancel')}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => submitStream()}
                      disabled={!applyButtonEnabled}
                      state={buttonState}
                    >
                      {t('general.apply')}
                    </Button>
                  </div>
                )}
              </DynamicContainer>

              <FormBuilder
                translationPrefixLabels="stream.fields"
                translationPrefixEnums="stream.enums"
                layout={FormLayout.THREE_COLUMNS_GRID}
                fields={[
                  {
                    type: FormFieldType.TITLE,
                    label: t('stream.sections.parameters'),
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'encapsulation',
                    options: encapsulationOptions,
                    onChange: (e) => {
                      if (e.target.value === StreamEncapsulation.SRT && values.overheadPercentage == 15) {
                        formContext.setFieldValue('overheadPercentage', 25);
                      } else if (e.target.value !== StreamEncapsulation.SRT && values.overheadPercentage == 25) {
                        formContext.setFieldValue('overheadPercentage', 15);
                      }

                      // NKE-3472 if (None) was selected and we remove it, switch to the first encoder
                      if (
                        e.target.value === StreamEncapsulation.WHIP &&
                        (values.video?.length == 0 || values.video[0]?.id === -1)
                      ) {
                        formContext.setFieldValue('video', [{ id: 0 }]);
                      }

                      // NKE-3642 if multiple audio encoders present, put back at a single one
                      if (e.target.value === StreamEncapsulation.WHIP && values.audio?.length > 1) {
                        formContext.setFieldValue('audio', [{ id: values.audio[0]?.id }]);
                      }

                      if (
                        e.target.value !== StreamEncapsulation.SRT &&
                        values.shaping === StreamShaping.CVBR &&
                        trafficShapingWasTweaked() !== false
                      ) {
                        formContext.setFieldValue('shaping', trafficShapingWasTweaked() as StreamShaping);
                        setTrafficShapingWasTweaked(false);
                      }

                      // NKE-3591
                      if (e.target.value === StreamEncapsulation.WHIP) {
                        setShowTsFields(false);
                      }
                    },
                  },
                  {
                    type: FormFieldType.SWITCH,
                    label: t('stream.showTsFields'),
                    className: 'mk-inline-toggler',
                    value: showTsFields,
                    onChange: (checked: boolean) => {
                      setShowTsFields(checked);
                    },
                    when: values.encapsulation !== StreamEncapsulation.WHIP,
                  },
                  {
                    type: FormFieldType.LINE_BREAK,
                  },
                  { type: FormFieldType.SECTION_START, when: showTsFields },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'videoPids',
                    placeholder: values.videoPidsPlaceholder ?? t('stream.pidAutoAssigned'),
                    hint: t('stream.fields.videoPidsExplain'),
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'audioPids',
                    placeholder: values.audioPidsPlaceholder ?? t('stream.pidAutoAssigned'),
                    hint: t('stream.fields.audioPidsExplain'),
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'metadataPids',
                    placeholder: values.metadataPidsPlaceholder ?? t('stream.pidAutoAssigned'),
                    hint: t('stream.fields.metadataPidsExplain'),
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'sctePids',
                    placeholder: values.sctePidsPlaceholder ?? t('stream.pidAutoAssigned'),
                    hint: t('stream.fields.sctePidsExplain'),
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'pcrPid',
                    placeholder: values.pcrPidPlaceholder ?? t('stream.pidAutoAssigned'),
                    hint: t('stream.fields.pcrPidExplain'),
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'pmtPid',
                    placeholder: values.pmtPidPlaceholder ?? t('stream.pidAutoAssigned'),
                    hint: t('stream.fields.pmtPidExplain'),
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'tsStreamId',
                    hint: t('stream.fields.tsStreamIdExplain'),
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'programNumber',
                    placeholder: values.programNumberPlaceholder ?? t('stream.pidAutoAssigned'),
                    hint: t('stream.fields.programNumberExplain'),
                  },
                  { type: FormFieldType.SECTION_END },
                ]}
              />

              <FormSection className="hai-mb-6" title={t('stream.sections.source')} key={t('stream.sections.source')}>
                <FormBuilder
                  translationPrefixLabels="stream.fields"
                  translationPrefixEnums="stream.enums"
                  layout={FormLayout.THREE_COLUMNS_GRID}
                  fields={[
                    {
                      type: FormFieldType.COLUMN_START,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'video[]',
                      label: t('stream.fields.video'),
                      options:
                        values.encapsulation === StreamEncapsulation.WHIP
                          ? videoEncoders.map((v) => ({ label: v.name, description: v.codecTxtLong, value: v.id }))
                          : [{ label: t('general.noneSelected'), description: '', value: -1 }].concat(
                              videoEncoders.map((v) => ({ label: v.name, description: v.codecTxtLong, value: v.id })),
                            ),
                      value: videoCount > 0 ? values.video[0].id : -1,
                      onChange: onChangeVideo,
                      addon: (
                        <>
                          {values.encapsulation === StreamEncapsulation.WHIP &&
                            values.video.length > 0 &&
                            videoEncoderSelected?.codecAlgorithm === VIDEO_CODEC.H265 && (
                              <div className="addon-warning-container" id="whip-videnc-warning">
                                {t('stream.videoEncoderH264Required')}
                              </div>
                            )}
                        </>
                      ),
                    },
                    {
                      type: FormFieldType.COLUMN_END,
                    },
                    {
                      type: FormFieldType.MULTI_DROPDOWN,
                      name: 'audio',
                      className: values.encapsulation === StreamEncapsulation.WHIP && 'mk-hide-add-button',
                      options:
                        audioCount <= 1
                          ? [{ label: t('general.noneSelected'), description: '', value: -1 }].concat(
                              audioEncoders.map((a) => ({
                                label: a.name,
                                description: t(`audioEncoder.algorithm.${a.algorithm}`),
                                value: a.id,
                              })),
                            )
                          : audioEncoders.map((a) => ({
                              label: a.name,
                              description: t(`audioEncoder.algorithm.${a.algorithm}`),
                              value: a.id,
                            })),
                      value: values.audio.map((a) => a.id),
                      setValues: (newValues: any) => {
                        formContext.setFieldValue(
                          'audio',
                          newValues.map((a: any, i: number) => ({ ...values.audio[i], id: a })),
                        );
                        newValues.forEach((v: any, index: Number) => {
                          formContext.setFieldValue('audio_multi[' + index + ']', v); // workaround for HAIUI dropdown which dont listen to defaultValue changes
                        });
                      },
                      maxAmount: disableAddAll || disableAudioAdd ? 0 : systemInfo.audioEncoders,
                      addon: (
                        <>
                          {values.encapsulation === StreamEncapsulation.WHIP &&
                            values.video.length > 0 &&
                            firstAudioEncoderSelected &&
                            firstAudioEncoderSelected?.algorithm !== AudioAlgorithmEnum.AUDIO_ALGO_OPUS && (
                              <div className="addon-warning-container" id="whip-audenc-warning">
                                {t('stream.audioEncoderOpusRequired')}
                              </div>
                            )}
                        </>
                      ),
                    },
                    {
                      type: FormFieldType.COLUMN_START,
                    },
                    {
                      type: FormFieldType.MULTI_DROPDOWN,
                      name: 'metadata',
                      options:
                        metadataCount <= 1
                          ? [{ label: t('general.noneSelected'), value: -1 }].concat(
                              metadataModels.map((v) => ({
                                label: metadataNameOrConstructed(v, t),
                                value: v.id,
                              })),
                            )
                          : metadataModels.map((v) => ({ label: metadataName(v, t), value: v.id })),
                      value: values.metadata.map((m) => m.id),
                      setValues: (newValues: any) => {
                        formContext.setFieldValue(
                          'metadata',
                          newValues.map((m: any, i: number) => ({ ...values.metadata[i], id: m })),
                        );
                        newValues.forEach((m: any, index: Number) => {
                          formContext.setFieldValue('metadata_multi[' + index + ']', m); // workaround for HAIUI dropdown which dont listen to defaultValue changes
                        });
                      },
                      when: systemInfoCapabilities.hasMetadata() && values.encapsulation !== StreamEncapsulation.WHIP,
                      maxAmount: disableAddAll || disableMetadataAdd ? 0 : metadataModels.length,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'metadataEncapsulation',
                      enum: MetadataEncapsulation,
                      when: isAnyMetadata && values.encapsulation !== StreamEncapsulation.WHIP,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'scte[]',
                      label: t('stream.fields.scte'),
                      options: [{ label: t('general.noneSelected'), value: -1 }].concat(
                        scteModels.map((v) => ({ label: v.name, value: v.id })),
                      ),
                      value: scteCount > 0 ? values.scte[0].id : -1,
                      onChange: onChangeScte,
                      breakBefore: true,
                      when: systemInfo.scte && values.encapsulation !== StreamEncapsulation.WHIP,
                    },
                    {
                      type: FormFieldType.COLUMN_END,
                    },
                  ]}
                />
              </FormSection>

              {values.encapsulation === StreamEncapsulation.WHIP && (
                <FormSection
                  className="hai-mb-6"
                  title={t('stream.sections.destinationWhip')}
                  key={t('stream.sections.destinationWhip')}
                >
                  <FormBuilder
                    translationPrefixLabels="stream.fields"
                    translationPrefixEnums="stream.enums"
                    layout={FormLayout.THREE_COLUMNS_GRID}
                    fields={[
                      {
                        type: FormFieldType.INPUT_TEXT,
                        name: 'url',
                        placeholder: t('stream.fields.urlHint'),
                        autoComplete: 'new-password', //"one-time-code",
                        span: 2,
                        required: true,
                      },
                      {
                        type: FormFieldType.INPUT_PASSWORD,
                        name: 'bearerToken',
                        autoComplete: 'new-password',
                        pwdToggle: true,
                        // hint: t('stream.fields.bearerToken'),
                      },
                      {
                        type: FormFieldType.DROPDOWN,
                        name: 'networkInterface',
                        label: t('stream.fields.networkInterface'),
                        enum: NetworkInterfaces,
                        when: values.encapsulation === StreamEncapsulation.WHIP && systemInfo.hasEth1,
                      },
                    ]}
                  />
                </FormSection>
              )}

              {values.encapsulation !== StreamEncapsulation.WHIP && (
                <FormBuilder
                  translationPrefixLabels="stream.fields"
                  translationPrefixEnums="stream.enums"
                  layout={FormLayout.THREE_COLUMNS_GRID}
                  fields={[
                    {
                      type: FormFieldType.TITLE,
                      label: t('stream.sections.destination'),
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'srtMode',
                      enum: SRTMode,
                      when: values.encapsulation === StreamEncapsulation.SRT,
                      onChange: (value: any) => {
                        fixSrtFields(value.target.value, values.srtRedundancyMode, values.authentication);
                        onChangeSrtMode(value);
                      },
                    },
                    {
                      type: FormFieldType.INPUT_NUMBER,
                      name: 'port',
                      mask: true,
                      hint: t('stream.fields.portExplain'),
                      when: values.encapsulation === StreamEncapsulation.SRT && values.srtMode === SRTMode.LISTENER,
                      required: true,
                    },
                    {
                      type: FormFieldType.INPUT_NUMBER,
                      name: 'srtListenerSecondPort',
                      mask: true,
                      placeholder: t('stream.srtListenerSecondPortPlaceholder'),
                      when: values.encapsulation === StreamEncapsulation.SRT && values.srtMode === SRTMode.LISTENER,
                      onChange: onChangeListenerSecondPort,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'networkInterface',
                      label: t('stream.fields.networkInterface'),
                      enum: NetworkInterfaces,
                      when: values.encapsulation !== StreamEncapsulation.SRT && systemInfo.hasEth1,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'srtRedundancyMode',
                      options: Object.values(SrtPathRedundancyModeOffered).map((value) => ({
                        value: value,
                        label: t(`stream.srtRedundancyOptions.${value}`),
                      })),
                      value: values.srtRedundancyMode,
                      when: values.encapsulation === StreamEncapsulation.SRT && values.srtMode === SRTMode.CALLER,
                    },
                    {
                      type: FormFieldType.SUBTITLE,
                      label: t('stream.sections.path1'),
                      when: showRedundancy,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'srtRedundancyPath1Role',
                      enum: SrtRedundancyPathRole,
                      when: showRedundancyPrimaryPathRows,
                    },
                    {
                      type: FormFieldType.INPUT_TEXT,
                      name: 'srtRedundancyPath1Name',
                      hint: t('stream.fields.srtRedundancyPath1NamePlaceholder'),
                      when: showRedundancy,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'networkInterface',
                      label: t('stream.fields.networkInterface'),
                      enum: NetworkInterfaces,
                      when: systemInfo.hasEth1 && values.encapsulation === StreamEncapsulation.SRT && !isListener,
                    },
                    {
                      type: FormFieldType.INPUT_TEXT,
                      name: 'address',
                      mask: true,
                      when: showRedundancy || !isListener,
                      required: true,
                    },
                    {
                      type: FormFieldType.INPUT_NUMBER,
                      name: showRedundancy ? 'srtRedundancyPath1SourcePort' : 'sourcePort',
                      mask: true,
                      placeholder: t('stream.fields.srtRedundancyPath1SourcePortPlaceholder'),
                      hint: t('stream.fields.portExplain'),
                      when: showRedundancy || showNonRedundantCallerRendezvous,
                      disabled: values.srtMode === SRTMode.RENDEZVOUS,
                    },
                    {
                      type: FormFieldType.INPUT_NUMBER,
                      name: 'port',
                      label: t('stream.fields.srtRedundancyPath1Port'),
                      mask: true,
                      hint: t('stream.fields.portExplain'),
                      when: showRedundancy || showNonRedundantCallerRendezvous,
                      required: true,
                      onChange: onChangePort,
                    },
                    {
                      type: FormFieldType.SUBTITLE,
                      label: t('stream.sections.path2'),
                      when:
                        values.encapsulation === StreamEncapsulation.SRT &&
                        values.srtMode === SRTMode.CALLER &&
                        (values.srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_ACTIVE ||
                          values.srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_BACKUP),
                    },
                    {
                      type: FormFieldType.STATIC_TEXT,
                      name: 'pathRole',
                      label: t('stream.fields.pathRole'),
                      value:
                        values.srtRedundancyPath1Role === 'secondary'
                          ? t('stream.enums.srtRedundancyRoleOptions.primary')
                          : t('stream.enums.srtRedundancyRoleOptions.secondary'),
                      when: showRedundancyPrimaryPathRows,
                    },
                    {
                      type: FormFieldType.INPUT_TEXT,
                      name: 'srtRedundancyPath2Name',
                      hint: t('stream.fields.srtRedundancyPath2NamePlaceholder'),
                      when: showRedundancy,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'srtRedundancyPath2NetworkInterface',
                      label: t('stream.fields.networkInterface'),
                      enum: NetworkInterfaces,
                      when: showRedundancy && systemInfo.hasEth1,
                    },
                    {
                      type: FormFieldType.INPUT_TEXT,
                      name: 'srtRedundancyPath2Address',
                      when: showRedundancy,
                      required: true,
                    },
                    {
                      type: FormFieldType.INPUT_NUMBER,
                      name: 'srtRedundancyPath2SourcePort',
                      placeholder: t('stream.fields.srtRedundancyPath2SourcePortPlaceholder'),
                      hint: t('stream.fields.portExplain'),
                      when: showRedundancy,
                    },
                    {
                      type: FormFieldType.INPUT_NUMBER,
                      name: 'srtRedundancyPath2Port',
                      mask: true,
                      hint: t('stream.fields.portExplain'),
                      when: showRedundancy,
                      required: true,
                    } /* TODO: conditional line break that supports "when" prop
                  {
                    type: FormFieldType.LINE_BREAK,
                    when: showRedundancy,
                  },*/,
                    {
                      type: FormFieldType.INPUT_NUMBER,
                      name: 'port',
                      mask: true,
                      hint:
                        values.encapsulation === StreamEncapsulation.RTP
                          ? t('stream.fields.portNumberRTPExplain')
                          : t('stream.fields.portExplain'),
                      required: true,
                      when:
                        values.encapsulation === StreamEncapsulation.UDP ||
                        values.encapsulation === StreamEncapsulation.RTP,
                    },
                    {
                      type: FormFieldType.TITLE,
                      label: t('stream.sections.srtSettings'),
                      when: values.encapsulation === StreamEncapsulation.SRT,
                    },
                    {
                      type: FormFieldType.INPUT_NUMBER,
                      name: 'latency',
                      hint: t('stream.fields.latencyExplain'),
                      when: values.encapsulation === StreamEncapsulation.SRT,
                      required: true,
                    },
                    {
                      type: FormFieldType.CHECKBOX,
                      name: 'adaptive',
                      when: values.encapsulation === StreamEncapsulation.SRT && videoEncoderRealCount > 0,
                      disabled: srtRedundantEnabled,
                      tooltip: srtRedundantEnabled ? t('stream.tooltips.adaptiveOffRedundant') : undefined,
                      onChange: onChangeNetworkAdaptive,
                    },
                    {
                      type: FormFieldType.LINE_BREAK,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'encKeyLength',
                      enum: SRTEncryption,
                      when: values.encapsulation === StreamEncapsulation.SRT,
                      onChange: () => {
                        fixSrtFields(values.srtMode, values.srtRedundancyMode, values.authentication);
                      },
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'authentication',
                      enum: SRTAuthenticationListener,
                      value: values.authentication /* WORKAROUND HAIUI ISSUE */,
                      when:
                        values.encapsulation === StreamEncapsulation.SRT &&
                        values.encKeyLength > 0 &&
                        values.srtMode === SRTMode.LISTENER,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'authentication',
                      enum: SRTAuthenticationOthers,
                      value: values.authentication /* WORKAROUND HAIUI ISSUE */,
                      when:
                        values.encapsulation === StreamEncapsulation.SRT &&
                        values.encKeyLength > 0 &&
                        values.srtMode !== SRTMode.LISTENER,
                    },
                    {
                      type: FormFieldType.INPUT_PASSWORD,
                      name: 'passphrase',
                      mask: true,
                      id: 'passphrase',
                      pwdToggle: sessionStore.isUser() ? false : true,
                      autoComplete: 'new-password',
                      placeholder: values.encrypted ? constant.obfuscatedPassphrase : undefined,
                      when: values.encapsulation === StreamEncapsulation.SRT && values.encKeyLength > 0,
                      required: true,
                    },
                    {
                      type: FormFieldType.TITLE,
                      label: t('stream.sections.srtAccessControl'),
                      when: values.encapsulation === StreamEncapsulation.SRT && values.srtMode === SRTMode.CALLER,
                    } /*
                  {
                    type: FormFieldType.SUBTITLE,
                    node: (<><FormSwitch
                    title={t('stream.form.srt.srtAccessControl')}
                    name={'srtStreamId'}
                    className="bold"
                    switchLabel={values.srtStreamId ? t('general.on') : t('general.off')}
                    onChange={() => {
                      if (
                        values.srtAccessControlPublishingIdType !== 'custom' &&
                        values.srtAccessControlPublishingIdType !== 'standard'
                      ) {
                        // This can happen on an existing non-SRT stream. The srtAccessControlPublishingIdType will not be returned by REST API so undefined.
                        values.srtAccessControlPublishingIdType = 'standard'; // default is standard
                      }
                    }}/></>),
                  },*/,
                    {
                      type: FormFieldType.NODE,
                      node: <StreamEditAccessControlForm ref={accessControlFormRef} {...formContext} />,
                    },
                  ]}
                />
              )}
              <FormBuilder
                key="stream-form-ttl-mtu"
                translationPrefixLabels="stream.fields"
                translationPrefixEnums="stream.enums"
                layout={FormLayout.THREE_COLUMNS_GRID}
                fields={[
                  {
                    type: FormFieldType.TITLE,
                    label: t('stream.sections.linkParameters'),
                  },
                  {
                    type: FormFieldType.STATIC_TEXT,
                    name: 'bandwithEstimate',
                    when: values.id !== -1 && values.encapsulation !== StreamEncapsulation.WHIP,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'fecRtp',
                    enum: FecRTP,
                    when: values.encapsulation === StreamEncapsulation.RTP,
                  },
                  {
                    type: FormFieldType.SECTION_START,
                    when: values.fecRtp === FecRTP.MPEG_PRO_FEC,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'level',
                    enum: MpegProFecLevel,
                    when: values.encapsulation === StreamEncapsulation.RTP,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'columns',
                    hint:
                      values.level === MpegProFecLevel.COLUMNS_ONLY
                        ? t('stream.fields.columnsExplainA')
                        : t('stream.fields.columnsExplainB'),
                    required: true,
                    when: values.encapsulation === StreamEncapsulation.RTP,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'rows',
                    hint: t('stream.fields.rowsExplain'),
                    required: values.level !== MpegProFecLevel.COLUMNS_ONLY,
                    disabled: values.level === MpegProFecLevel.COLUMNS_ONLY,
                    when: values.encapsulation === StreamEncapsulation.RTP,
                  },
                  {
                    type: FormFieldType.CHECKBOX,
                    name: 'blockAligned',
                    when: values.encapsulation === StreamEncapsulation.RTP,
                  },
                  {
                    type: FormFieldType.SECTION_END,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'shaping',
                    enum: StreamShaping,
                    when: values.encapsulation !== StreamEncapsulation.WHIP,
                    disabled: values.adaptive == true && values.encapsulation === StreamEncapsulation.SRT,
                    onChange: onChangeTrafficShaping,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'metadataBandwidth',
                    placeholder: values.metadataBandwidthPlaceholder,
                    when: isAnyMetadata && values.shaping !== StreamShaping.VBR,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'overheadPercentage',
                    hint:
                      values.encapsulation === StreamEncapsulation.SRT
                        ? t('stream.fields.overheadPercentageSrtExplain')
                        : t('stream.fields.overheadPercentageExplain'),
                    required: true,
                    when: values.encapsulation === StreamEncapsulation.SRT || values.shaping !== StreamShaping.VBR,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'mtu',
                    placeholder: '1496',
                    hint: t('stream.fields.mtuExplain'),
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'tos',
                    placeholder: '0x80',
                    hint: t('stream.fields.tosExplain'),
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'ttl',
                    placeholder: '64',
                    hint: t('stream.fields.ttlExplain'),
                  },
                ]}
              />
              {
                systemInfo.storage && (
                  <FormBuilder
                    key="stream-form-recording"
                    translationPrefixLabels="stream.fields"
                    translationPrefixEnums="stream.enums"
                    layout={FormLayout.THREE_COLUMNS_GRID}
                    fields={[
                      {
                        type: FormFieldType.TITLE,
                        label: t('stream.sections.recording'),
                      },
                      {
                        type: FormFieldType.SWITCH,
                        name: 'recording.enabled',
                        label: t('stream.sections.recordingToggle'),
                      },
                      {
                        type: FormFieldType.LINE_BREAK,
                      },
                      {
                        type: FormFieldType.INPUT_TEXT,
                        name: 'recording.name',
                        when: values.recording.enabled === true,
                        //  when: values.id !== -1 && values.encapsulation !== StreamEncapsulation.WHIP,
                      },
                      {
                        type: FormFieldType.DROPDOWN,
                        name: 'recording.format',
                        enum: RecordFormat,
                        disabled: true,
                        when: values.recording.enabled === true,
                        //   when: values.encapsulation === StreamEncapsulation.RTP,
                      },
                      {
                        type: FormFieldType.CHECKBOX,
                        name: 'recording.autoStart',
                        when: values.recording.enabled === true,
                      },
                      {
                        type: FormFieldType.LINE_BREAK,
                        when: values.recording.enabled === true,
                      },
                      {
                        type: FormFieldType.DROPDOWN,
                        name: 'recording.limitType',
                        enum: RecordLimitType,
                        when: values.recording.enabled === true,
                        //      when: values.id !== -1 && values.encapsulation !== StreamEncapsulation.WHIP,
                      },
                      {
                        type: FormFieldType.NODE,
                        node: (
                          <div key="record-limits-size" className="mk-form-custom-two-checkbox-fields sixtyforty">
                            <FormBuilder
                              translationPrefixLabels="stream.fields"
                              translationPrefixEnums="stream.enums"
                              layout={FormLayout.PLAIN_FIELDS}
                              fields={[
                                {
                                  type: FormFieldType.INPUT_NUMBER_ARROWS,
                                  name: 'recording.limitSize',
                                  min: 0,
                                },
                                {
                                  type: FormFieldType.DROPDOWN,
                                  name: 'recording.limitSizeUnit',
                                  enum: RecordLimitSizeUnit,
                                },
                              ]}
                            />
                          </div>
                        ),
                        when: values.recording.enabled === true && values.recording.limitType === RecordLimitType.SIZE,
                      },
                      {
                        type: FormFieldType.NODE,
                        node: (
                          <div key="record-limits-time" className="mk-form-custom-two-checkbox-fields">
                            <FormBuilder
                              translationPrefixLabels="stream.fields"
                              translationPrefixEnums="stream.enums"
                              layout={FormLayout.PLAIN_FIELDS}
                              fields={[
                                {
                                  type: FormFieldType.INPUT_NUMBER_ARROWS,
                                  name: 'recording.limitSizeHour',
                                  min: 0,
                                },
                                {
                                  type: FormFieldType.INPUT_NUMBER_ARROWS,
                                  name: 'recording.limitSizeMinutes',
                                  min: 0,
                                },
                              ]}
                            />
                          </div>
                        ),
                        when: values.recording.enabled === true && values.recording.limitType === RecordLimitType.TIME,
                      },
                      {
                        type: FormFieldType.LINE_BREAK,
                        when: values.recording.enabled === true,
                      },
                      {
                        type: FormFieldType.DROPDOWN,
                        name: 'recording.segmentType',
                        enum: RecordLimitType,
                        when: values.recording.enabled === true,
                        //      when: values.id !== -1 && values.encapsulation !== StreamEncapsulation.WHIP,
                      },
                      {
                        type: FormFieldType.NODE,
                        node: (
                          <div key="record-segmentations" className="mk-form-custom-two-checkbox-fields">
                            <FormBuilder
                              translationPrefixLabels="stream.fields"
                              translationPrefixEnums="stream.enums"
                              layout={FormLayout.PLAIN_FIELDS}
                              fields={[
                                {
                                  type: FormFieldType.INPUT_NUMBER_ARROWS,
                                  name: 'recording.segmentHour',
                                  min: 0,
                                },
                                {
                                  type: FormFieldType.INPUT_NUMBER_ARROWS,
                                  name: 'recording.segmentMinutes',
                                  min: 0,
                                },
                              ]}
                            />
                          </div>
                        ),
                        when:
                          values.recording.enabled === true && values.recording.segmentType === RecordLimitType.TIME,
                      },
                      {
                        type: FormFieldType.NODE,
                        node: (
                          <div key="record-segmentation-size" className="mk-form-custom-two-checkbox-fields sixtyforty">
                            <FormBuilder
                              translationPrefixLabels="stream.fields"
                              translationPrefixEnums="stream.enums"
                              layout={FormLayout.PLAIN_FIELDS}
                              fields={[
                                {
                                  type: FormFieldType.INPUT_NUMBER_ARROWS,
                                  name: 'recording.segmentSize',
                                  min: 0,
                                },
                                {
                                  type: FormFieldType.DROPDOWN,
                                  name: 'recording.segmentSizeUnit',
                                  enum: RecordLimitSizeUnit,
                                },
                              ]}
                            />
                          </div>
                        ),
                        when:
                          values.recording.enabled === true && values.recording.segmentType === RecordLimitType.SIZE,
                      },
                      {
                        type: FormFieldType.CHECKBOX,
                        name: 'recording.recycleSegments',
                        when:
                          values.recording.enabled === true &&
                          values.recording.limitType !== RecordLimitType.NONE && // segmentation recycling requires a global limit
                          (values.recording.segmentType === RecordLimitType.TIME ||
                            values.recording.segmentType === RecordLimitType.SIZE),
                      },
                    ]}
                  />
                ) /* end storage */
              }
              {values.encapsulation !== StreamEncapsulation.SRT &&
                values.encapsulation !== StreamEncapsulation.WHIP && (
                  <FormBuilder
                    key="stream-form-sap"
                    translationPrefixLabels="stream.fields"
                    translationPrefixEnums="stream.enums"
                    layout={FormLayout.THREE_COLUMNS_GRID}
                    fields={[
                      {
                        type: FormFieldType.TITLE,
                        label: t('stream.sections.sap'),
                        className: 'hai-mb-0',
                      },
                      {
                        type: FormFieldType.SWITCH,
                        name: 'sap.advertise',
                      },
                      {
                        type: FormFieldType.LINE_BREAK,
                      },
                      {
                        type: FormFieldType.SECTION_START,
                        when: values.sap.advertise,
                      },
                      {
                        type: FormFieldType.INPUT_TEXT,
                        name: 'sap.name',
                        mask: true,
                        required: true,
                      },
                      {
                        type: FormFieldType.INPUT_TEXT,
                        name: 'sap.desc',
                        mask: true,
                      },
                      {
                        type: FormFieldType.INPUT_TEXT,
                        name: 'sap.author',
                        mask: true,
                      },
                      {
                        type: FormFieldType.INPUT_TEXT,
                        name: 'sap.keywords',
                        mask: true,
                      },
                      {
                        type: FormFieldType.INPUT_TEXT,
                        name: 'sap.copyright',
                        mask: true,
                      },
                      {
                        type: FormFieldType.INPUT_TEXT,
                        name: 'sap.address',
                        mask: true,
                        value: values.sap.addressConfigured ?? '',
                        placeholder: values.sapAddressPlaceholder ?? t('general.autoAssign'),
                      },
                      {
                        type: FormFieldType.INPUT_NUMBER,
                        name: 'sap.port',
                        mask: true,
                        placeholder: '9875',
                      },
                      {
                        type: FormFieldType.SECTION_END,
                      },
                    ]}
                  />
                )}
            </>
          );
        }}
      </FormContext.Consumer>
    </Form>
  );
});

StreamEdit.displayName = 'StreamEdit';
